import React from "react";
import { CUISINES, TYPES } from "../data/spots";

export interface FilterState {
  query: string;
  cuisine: string | null;
  type: string | null;
  sort: "recommended" | "rating" | "name";
}

export type ViewMode = "list" | "map";

interface Props {
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  view: ViewMode;
  setView: (v: ViewMode) => void;
  resultCount: number;
}

const Toolbar: React.FC<Props> = ({
  filters,
  setFilters,
  view,
  setView,
  resultCount,
}) => {
  const toggleCuisine = (c: string) =>
    setFilters((f) => ({
      ...f,
      cuisine: f.cuisine === c ? null : c,
    }));

  const toggleType = (t: string) =>
    setFilters((f) => ({
      ...f,
      type: f.type === t ? null : t,
    }));

  const clearAll = () =>
    setFilters({ query: "", cuisine: null, type: null, sort: "recommended" });

  const hasFilters =
    filters.query !== "" || filters.cuisine !== null || filters.type !== null;

  return (
    <div className="toolbar-wrap">
      <div className="toolbar">
        <div className="toolbar__row">
          <div className="search">
            <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
            <input
              type="search"
              aria-label="Search spots by name, area, or dish"
              placeholder="Search by name, area, or dish"
              value={filters.query}
              onChange={(e) =>
                setFilters((f) => ({ ...f, query: e.target.value }))
              }
            />
          </div>
          <div className="view-toggle" role="group" aria-label="View mode">
            <button
              className={view === "list" ? "active" : ""}
              onClick={() => setView("list")}
              aria-pressed={view === "list"}
            >
              <i className="fa-solid fa-list" aria-hidden="true" />
              List
            </button>
            <button
              className={view === "map" ? "active" : ""}
              onClick={() => setView("map")}
              aria-pressed={view === "map"}
            >
              <i className="fa-solid fa-map" aria-hidden="true" />
              Map
            </button>
          </div>
          <select
            className="sort-select"
            aria-label="Sort spots"
            value={filters.sort}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                sort: e.target.value as FilterState["sort"],
              }))
            }
          >
            <option value="recommended">Recommended</option>
            <option value="rating">Highest rated</option>
            <option value="name">Name (A-Z)</option>
          </select>
        </div>

        <div className="filters" role="group" aria-label="Filter by cuisine">
          {TYPES.map((t) => (
            <button
              key={t}
              className={"chip" + (filters.type === t ? " active" : "")}
              onClick={() => toggleType(t)}
              aria-pressed={filters.type === t}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="filters" role="group" aria-label="Filter by cuisine style">
          {CUISINES.map((c) => (
            <button
              key={c}
              className={"chip" + (filters.cuisine === c ? " active" : "")}
              onClick={() => toggleCuisine(c)}
              aria-pressed={filters.cuisine === c}
            >
              {c}
            </button>
          ))}
          {hasFilters && (
            <button className="chip chip--clear" onClick={clearAll}>
              <i className="fa-solid fa-xmark" aria-hidden="true" />
              Clear filters
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Toolbar;